/**
 * Errors announce themselves: an animation and a sound, the moment a NEW error
 * plate appears on screen.
 *
 * ─── Why this watches the DOM ───────────────────────────────────────────────
 * Errors are drawn in a dozen places (six windows, the Survivor Record, the
 * house, the duel HUD, Settings) by as many different bits of code, most of
 * which rebuild their markup with `innerHTML`. Hooking each would be a dozen
 * edits and a guarantee that the next window is silent. One observer, matching
 * on the one list of error classes below, covers every existing plate and any
 * that gets added with a listed class.
 *
 * ─── The re-render trap ─────────────────────────────────────────────────────
 * Several windows repaint themselves on a timer (the shop's restock clock ticks
 * every second), and a repaint rebuilds the error plate as a brand-new node
 * with the same text. Animating every new node would replay the shake once a
 * second for as long as an error stood on screen. So a message identical to one
 * seen within `SAME_MESSAGE_MS` is treated as the same plate repainted: no
 * animation and no sound. Each repaint refreshes the window, so a standing error
 * stays quiet however long it stands.
 *
 * The animation itself is a class the CSS keys on (`bv-err-in`, see
 * `styles/errors.css.ts`) rather than a default on the plate, so a plate that
 * this module classifies as a repaint simply never gets it.
 */
import type { MixerHandle } from './mixer';
import { playErrorSound, resetErrorSoundWindow } from './ui-sounds';

/** Every error plate in the game. Add a class here and it animates and sounds. */
export const ERROR_SELECTOR = '.bv-win-err, .bv-pd-msg, .bv-home-error, .bv-duel-error, .tg-set-msg-err';

/** An identical message inside this window is a repaint, not a new error. */
export const SAME_MESSAGE_MS = 1500;

export function createErrorFx(mixer: MixerHandle): () => void {
  resetErrorSoundWindow();
  const lastSeen = new Map<string, number>();

  const announce = (el: HTMLElement): void => {
    if (el.hidden || !el.isConnected) return;
    const text = (el.textContent ?? '').trim();
    if (!text) return;
    el.setAttribute('role', 'alert');

    const now = performance.now();
    const before = lastSeen.get(text);
    lastSeen.set(text, now);
    // Forget old messages so the map cannot grow without bound over a session.
    if (lastSeen.size > 40) {
      for (const [k, t] of lastSeen) if (now - t > SAME_MESSAGE_MS) lastSeen.delete(k);
    }
    if (before !== undefined && now - before < SAME_MESSAGE_MS) return;

    // Restart the animation even if this same node already played it (the
    // Record reuses one message element for every error it shows).
    el.classList.remove('bv-err-in');
    void el.offsetWidth;
    el.classList.add('bv-err-in');
    el.addEventListener('animationend', () => el.classList.remove('bv-err-in'), { once: true });
    playErrorSound(mixer);
  };

  const hostOf = (n: Node): HTMLElement | null => {
    const el = n.nodeType === Node.ELEMENT_NODE ? (n as HTMLElement) : n.parentElement;
    return el?.closest?.(ERROR_SELECTOR) as HTMLElement | null;
  };

  const observer = new MutationObserver((records) => {
    const found = new Set<HTMLElement>();
    for (const r of records) {
      if (r.type === 'attributes') {
        const host = hostOf(r.target);
        if (host) found.add(host);
        continue;
      }
      // A plate rebuilt or inserted...
      r.addedNodes.forEach((n) => {
        const host = hostOf(n);
        if (host) found.add(host);
        if (n.nodeType === Node.ELEMENT_NODE) {
          (n as HTMLElement).querySelectorAll(ERROR_SELECTOR).forEach((e) => found.add(e as HTMLElement));
        }
      });
      // ...or an existing plate whose text was replaced.
      const host = hostOf(r.target);
      if (host) found.add(host);
    }
    found.forEach(announce);
  });
  observer.observe(document.body, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'],
  });
  return () => observer.disconnect();
}
